/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { EntityVersion, ID } from '@gh/common-shared-data';

import {
  AbstractRestService,
  arrayOf,
  identity,
  none,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { parametrizeUrl } from '@gh/core-util';

import { Observable } from 'rxjs';
import { ScheduleDetailsDto } from '../schedule-templates/schedule-template.dto';

import {
  MaintenanceScheduleFilterDto,
  MaintenanceScheduleListItemDto,
  ScheduleTaskDetailsDto,
} from './schedule.dto';
import {
  MaintenanceScheduleListItemMapper,
  ScheduleDetailsMapper,
  ScheduleTaskDetailsMapper,
} from './schedule.mapper';
import { Confirmation } from '@gh/core-ui';

@Injectable()
export class MaintenanceScheduleRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  findAllAsDataSource(filter: MaintenanceScheduleFilterDto): SlicedDataSource<MaintenanceScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/maintenances`,
      filter,
      identity(),
      arrayOf(MaintenanceScheduleListItemMapper));
  }

  findAllAsFinderDataSource(filter: MaintenanceScheduleFilterDto): SlicedDataSource<MaintenanceScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/maintenances/finder`,
      filter,
      identity(),
      arrayOf(MaintenanceScheduleListItemMapper));
  }

  remove(scheduleId: ID, version: EntityVersion): Observable<void> {
    return this.httpDelete(
      parametrizeUrl(`${this.bp}/schedules/maintenances/${scheduleId}`, { version }),
      void 0,
      none(),
      none());
  }

  init(jobId: ID, templateId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/maintenances/init`, { jobId, templateId }, identity(), ScheduleDetailsMapper);
  }

  get(jobId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/maintenances/${jobId}`, ScheduleDetailsMapper);
  }

  getCombined(jobIds: ID[]): Observable<ScheduleDetailsDto[]> {
    return this.httpGet(
      parametrizeUrl(`${this.bp}/schedules/maintenances/combined-gantt-chart`, { id: jobIds }),
      arrayOf(ScheduleDetailsMapper));
  }

  create(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/maintenances`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPost(url, schedule, ScheduleDetailsMapper);
  }

  update(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/maintenances/${schedule.scheduleId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url, schedule, ScheduleDetailsMapper);
  }

  recalculate(schedule: ScheduleDetailsDto): Observable<ScheduleDetailsDto> {
    return this.httpPut(`${this.bp}/schedules/maintenances/recalculate`, schedule, ScheduleDetailsMapper);
  }

  getTask(scheduleId: ID, taskId: ID): Observable<ScheduleTaskDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/maintenances/${scheduleId}/tasks/${taskId}`, ScheduleTaskDetailsMapper);
  }

  updateTask(scheduleId: ID, taskId: ID, task: ScheduleTaskDetailsDto, saveBaseDatesAnswer?: Confirmation):
    Observable<ScheduleTaskDetailsDto> {
    let url = `${this.bp}/schedules/maintenances/${scheduleId}/tasks/${taskId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url, task, ScheduleTaskDetailsMapper);
  }
}
